import { useState, useEffect } from "react";
import { ReportLog } from "../api/Reports";
import { BadgeColors } from "../shared-components/Badge";
import {
  classNames,
  formattedDateLongTz,
  LONG_TEXT_WRAP_CLASSES,
} from "../utils";
import { CogIcon, UserCircleIcon } from "@heroicons/react/24/solid";
import Container from "../shared-components/Container";

export interface LogsEntry {
  text: string;
  badgeColor?: BadgeColors;
  component?: JSX.Element;
}

interface LogsProps {
  data: ReportLog[];
  header: Array<string>;
}

const LogsComponent = ({ data, header }: LogsProps) => {
  const [logsData, setLogsData] = useState<ReportLog[]>(
    data !== undefined ? data : []
  );

  useEffect(() => {
    let logs: ReportLog[] = [];
    if (data) {
      logs = [...data];
    }
    setLogsData(logs);
  }, [data]);

  return (
    <div>
      <div>
        {header.map((header) => (
          <div key={header}>{header}</div>
        ))}
      </div>
      <Container>
        {logsData.length === 0 ? (
          <div className="flex justify-center">No logs to show.</div>
        ) : (
          logsData.map((report, i) => (
            <LogRow
              logRow={report}
              index={i}
              key={i}
              showMargin={logsData.length - 1 > i}
            />
          ))
        )}
      </Container>
    </div>
  );
};

interface LogRowProps {
  logRow: ReportLog;
  index: number;
  showMargin?: boolean;
}

const LogRow = ({ logRow, index, showMargin = true }: LogRowProps) => {
  return (
    <div>
      <div
        className={classNames("flex gap-4 text-sm items-start min-w-0")}
        key={logRow.external_id || `report-${index}`}
      >
        <div className="w-10 flex-shrink-0">
          {logRow.is_user ? (
            <UserCircleIcon className="p-1 w-7 h-7 rounded-xl bg-slate-200	" />
          ) : (
            <CogIcon className="p-1 gap-4 w-7 h-7 rounded-xl bg-slate-200	" />
          )}
        </div>

        <div
          className={classNames(
            "w-full max-w-[11rem] flex-shrink-0 text-gray-500 sm:max-w-[10rem]",
            LONG_TEXT_WRAP_CLASSES
          )}
        >
          {formattedDateLongTz(logRow.created_at)}
        </div>
        <div
          className={classNames(
            "min-w-0 flex-1 basis-[8rem] text-gray-900 font-medium",
            LONG_TEXT_WRAP_CLASSES
          )}
        >
          {logRow.name}
        </div>
        <div
          className={classNames(
            "min-w-0 flex-[1.5] basis-[10rem] text-gray-900 font-medium whitespace-pre-wrap",
            LONG_TEXT_WRAP_CLASSES
          )}
        >
          {logRow.log_message}
        </div>
      </div>
      {showMargin && <div className="w-0.5 h-12 bg-gray-200 ml-3 my-2"></div>}
    </div>
  );
};

export default LogsComponent;
